import { effectiveFormulaValue, resolveFormulas } from './formula';
import { DisplayedValues, EditedKeys, OfferLetterFormData } from './types';

/**
 * What a filled-in offer form submits, in both key spaces.
 *
 * Extracted from the Send Offer container so the offer review panel's edit form
 * (AXHE-4759) submits byte-identical payloads. The panel writes through the same
 * two endpoints, so a second resolution of "what did the recruiter mean by this
 * form" would be a second set of answers to one question — and the one that
 * diverges is whichever surface is edited next.
 */

/**
 * The answers for this template's fields — what the person entered, else the
 * prefill the server derived. EVERY field, including the ones the form is no
 * longer asking for.
 *
 * ## Why this does not prune (jdmurphy, #3372)
 *
 * It used to send only `visibleFields(...)`, which was the same rule the server
 * applies at its own boundary — and that duplication was the bug, not the
 * safeguard. `FieldVisibilityResolver.visible_values` is the authority and runs
 * on every write path, so pruning here decided nothing the server would not
 * decide again; what it DID do was hand the server a different input than the
 * one the form resolved itself from. `resolveDataKeyValues` reads every field,
 * so a mapped field withdrawn by the section rule still steered the client's
 * block resolution and then wasn't sent — and the server, resolving from a
 * payload missing that key, could exclude a SIBLING section and silently drop
 * what was typed into it.
 *
 * Sending everything makes the server's overlay identical to `dataKeyValues` by
 * construction. Nothing leaks by doing so:
 *
 *   - a field hidden by its OWN conditions still has its answer dropped —
 *     `visible_values` excludes `own_condition_visibility`'s failures before
 *     anything is persisted, so the "revealed it, typed, changed my mind" case
 *     is still enforced, just in the one place that can actually guarantee it
 *     against a stale tab or a direct API client;
 *   - a field withdrawn by the SECTION rule keeps its answer deliberately
 *     (AXHE-4187, `7cf7640`): every occurrence of it sits under an excluded
 *     block, so it prints nothing — the guarantee is discharged by the section
 *     being excluded, not by dropping the value. Keeping it is what makes the
 *     sections resolve the same way on a re-render as they did in the preview.
 *
 * `visibleFields` is still the rule for what the reader SEES (`SendOfferForm`).
 * Hiding the input is the ticket; deciding the payload never was.
 */
export const effectiveValues = (
  form: OfferLetterFormData | undefined,
  values: Record<string, string>,
): Record<string, string> => {
  if (!form) return {};
  // AXHE-4543. The persistence seam: an untouched (including cleared — see
  // `effectiveFormulaValue`) formula field's computed value goes out as an
  // ordinary entry here, exactly as if it had been typed —
  // `MergeValueResolver#raw_value_for` returns whatever arrives in
  // `manual_values[field.key]` verbatim. Resolving only in `SendOfferForm`'s
  // render would show the right number on screen and still generate the letter
  // with the field blank.
  const formulaValues = resolveFormulas(form, values);
  const merged: Record<string, string> = {};
  form.fields.forEach(field => {
    merged[field.key] = effectiveFormulaValue(field, values, formulaValues);
  });
  return merged;
};

/**
 * The answers for the data keys driving this template's conditional sections.
 *
 * Sends the resolved value for every condition field — what was entered, else
 * the prefill the server derived from the applicant record — mirroring
 * `effectiveValues` above. Sending only touched keys would leave the rest to
 * re-resolve server-side, which is fine today but diverges the moment AXHE-4083
 * starts writing values back: the offer should record the answers it was
 * generated under, not a subset.
 *
 * Scoped to the CURRENT template's condition fields because switching templates
 * keeps the entered state, and a data key from the previous template would
 * otherwise be submitted for a section that no longer exists.
 *
 * No section-withdrawn drivers are re-injected here (AXHE-4187). They used to
 * be: a mapped field the section rule withdrew had its answer put back under its
 * DATA KEY, because `effectiveValues` pruned it out of `field_values` and a
 * sibling section keyed on that key would otherwise resolve differently on the
 * server than it had on screen. `effectiveValues` no longer prunes, so the
 * answer arrives under its own field key and `visible_values` keeps it — one
 * channel, one key space, and no subset of the discarded answers coming back
 * through a map the server's overlay ranks ABOVE mapped field values.
 */
export const effectiveConditionValues = (
  form: OfferLetterFormData | undefined,
  conditionValues: Record<string, string>,
): Record<string, string> => {
  if (!form) return {};
  const merged: Record<string, string> = {};
  form.condition_fields.forEach(field => {
    merged[field.key] = conditionValues[field.key] ?? field.value;
  });
  return merged;
};

/**
 * Which controls the recruiter moved off the value the form displayed
 * (AXHE-4673), in both key spaces.
 *
 * ## Why the client has to say this at all
 *
 * Write-back needs to tell "the recruiter chose blank" from "blank is what we
 * showed them", and only this side knows. A condition input may display a value
 * that differs from the applicant's stored one, and an untouched submit sends
 * the displayed value back. Nothing on the server can reconstruct that nobody
 * touched the control.
 *
 * ## Changed, not merely touched
 *
 * Computed against the value each control OPENED ON, rather than from an
 * onChange flag, so a recruiter who opens a dropdown and picks the same answer
 * back reports nothing. It also picks up an untouched formula field whose
 * computed value moved because a field it derives from changed (AXHE-4543):
 * `effectiveValues` submits that number as if it had been typed, so the
 * applicant should follow it.
 *
 * ## `displayed` is the baseline, and it is not always the form's own prefill
 *
 * `field.value` is the right baseline only for a surface that RENDERS the form
 * payload as it arrived — `containers/SendOffer`, whose entered state starts
 * empty and whose form endpoint echoes a resumable draft's stored answers back
 * into it.
 *
 * The approval panel's edit form does not (AXHE-4759): `FindResumableOffer`
 * excludes `pending_approval` and `approved`, the two statuses an approver edits
 * from, so its form payload carries the TEMPLATE's prefill while the panel
 * renders the OFFER's stored answers. Comparing against `field.value` there
 * reports every control whose stored answer has since diverged from a fresh
 * prefill — including ones nobody touched, because an ordinary edit to the
 * applicant record moves the prefill on its own. That is the untouched-control
 * wipe this whole mechanism exists to prevent, so that caller passes what it
 * actually displayed and this compares against that.
 *
 * A key absent from `displayed` falls back to `field.value`, which is what the
 * panel itself renders for a control the offer has no answer for.
 *
 * ## The server does not take any of this on trust
 *
 * It recomputes what it would have shown and writes only where both answers
 * agree; a contradiction is reported and written nowhere. So this narrows what
 * is written and can never widen it.
 *
 * Both maps are read through the same resolvers the payload uses, so what is
 * reported and what is submitted cannot come from two different value sets.
 */
export const editedKeys = (
  form: OfferLetterFormData | undefined,
  values: Record<string, string>,
  conditionValues: Record<string, string>,
  displayed: DisplayedValues = {},
): EditedKeys => {
  if (!form) return { field_values: [], condition_values: [] };

  const fields = effectiveValues(form, values);
  const conditions = effectiveConditionValues(form, conditionValues);

  return {
    field_values: form.fields
      .filter(field => fields[field.key] !== (displayed.field_values?.[field.key] ?? field.value))
      .map(field => field.key),
    condition_values: form.condition_fields
      .filter(
        field =>
          conditions[field.key] !== (displayed.condition_values?.[field.key] ?? field.value),
      )
      .map(field => field.key),
  };
};

/**
 * The template field keys a form resolves its sections under (`mappedKeyAnswers`):
 * the edit report the offer it saves to already holds, joined with this
 * session's. The save stores that union (`OfferLetter#merge_edited_keys`), and
 * the form preview endpoint takes the same one, so the form and the server name
 * the same deciding copy before and after the save.
 */
export const sectionFieldKeys = (stored: string[], session: EditedKeys): Set<string> =>
  new Set([...stored, ...session.field_values]);
